import { useCallback, useState } from 'react'
import type { CountryCode, Currency, Entry, EntryMetadata, EntryType } from '@/types/domain'
import { defaultConverter } from '@/core/calculation/converter'
import { editingInvalidatesSnapshot, emptySnapshot } from '@/core/calculation/snapshot'
import { useEntriesStore, useSettingsStore } from '@/core/storage/zustand-store'

export interface EntryFormValues {
  tipo: EntryType
  dataRecebimento: string
  moedaOrigem: Currency
  valorOriginal: number
  pagador?: string
  categoria?: string
  tag?: string
  descricao?: string
}

export interface BulkRecalcProgress {
  total: number
  done: number
}

const COUNTRY_OF: Record<Currency, CountryCode> = { USD: 'US', CAD: 'CA' }

export interface UseEntryMutationsApi {
  createEntry: (values: EntryFormValues) => Entry
  updateEntry: (id: string, values: EntryFormValues) => Entry | null
  deleteEntry: (id: string) => void
  recalculateEntry: (id: string) => Promise<void>
  recalculateAll: () => Promise<void>
  /**
   * Rename the pagador across every entry that currently matches `oldName`.
   * Comparison is exact after trimming whitespace. Returns the number of
   * entries that were updated. The known-pagadores autocomplete list in
   * Settings is updated to drop the old name and add the new one.
   */
  renamePagador: (oldName: string, newName: string) => number
  /** How many entries would be touched by a renamePagador(oldName) call. */
  countEntriesByPagador: (name: string) => number
  bulkProgress: BulkRecalcProgress | null
}

export interface UseEntryMutationsOptions {
  converter?: { convert: typeof defaultConverter.convert }
}

/**
 * Single mutation surface for the entries store. All write paths flow
 * through here so the snapshot lifecycle (compute on create, invalidate +
 * recompute on input edit, preserve on metadata edit, recompute on demand)
 * lives in one file.
 *
 * Store actions are pulled via primitive selectors so the hook does not
 * re-render every time `entries` changes.
 */
export function useEntryMutations(options: UseEntryMutationsOptions = {}): UseEntryMutationsApi {
  const converter = options.converter ?? defaultConverter

  // Actions are stable references in Zustand — selecting them individually
  // keeps the hook output stable across unrelated state updates.
  const addEntry = useEntriesStore((s) => s.add)
  const updateEntryAction = useEntriesStore((s) => s.update)
  const removeEntry = useEntriesStore((s) => s.remove)
  const patchSettings = useSettingsStore((s) => s.patch)

  const [bulkProgress, setBulkProgress] = useState<BulkRecalcProgress | null>(null)

  const rememberMetadata = useCallback(
    (metadata: EntryMetadata) => {
      const settings = useSettingsStore.getState().settings
      const next = {
        knownPagadores: addUnique(settings.knownPagadores, metadata.pagador),
        knownCategorias: addUnique(settings.knownCategorias, metadata.categoria),
        knownTags: addUnique(settings.knownTags, metadata.tag),
      }
      if (
        next.knownPagadores !== settings.knownPagadores ||
        next.knownCategorias !== settings.knownCategorias ||
        next.knownTags !== settings.knownTags
      ) {
        patchSettings(next)
      }
    },
    [patchSettings],
  )

  const computeAndApply = useCallback(
    async (id: string) => {
      const target = useEntriesStore.getState().entries.find((e) => e.id === id)
      if (!target) return
      const snapshot = await converter.convert({
        moedaOrigem: target.moedaOrigem,
        valorOriginal: target.valorOriginal,
        dataRecebimento: target.dataRecebimento,
      })
      updateEntryAction(id, { snapshot })
    },
    [converter, updateEntryAction],
  )

  const rememberDefaultCurrency = useCallback(
    (currency: Currency) => {
      const settings = useSettingsStore.getState().settings
      if (settings.defaultCurrency !== currency) {
        patchSettings({ defaultCurrency: currency })
      }
    },
    [patchSettings],
  )

  const createEntry = useCallback(
    (values: EntryFormValues): Entry => {
      const now = new Date().toISOString()
      const entry: Entry = {
        id: crypto.randomUUID(),
        anoBase: Number.parseInt(values.dataRecebimento.slice(0, 4), 10),
        tipo: values.tipo,
        dataRecebimento: values.dataRecebimento,
        moedaOrigem: values.moedaOrigem,
        valorOriginal: values.valorOriginal,
        pais: COUNTRY_OF[values.moedaOrigem],
        pagador: values.pagador,
        categoria: values.categoria,
        tag: values.tag,
        descricao: values.descricao,
        criadoEm: now,
        atualizadoEm: now,
        snapshot: emptySnapshot(),
      }
      addEntry(entry)
      rememberMetadata(entry)
      rememberDefaultCurrency(values.moedaOrigem)
      void computeAndApply(entry.id)
      return entry
    },
    [addEntry, computeAndApply, rememberDefaultCurrency, rememberMetadata],
  )

  const updateEntry = useCallback(
    (id: string, values: EntryFormValues): Entry | null => {
      const current = useEntriesStore.getState().entries.find((e) => e.id === id)
      if (!current) return null

      const patch: Partial<Entry> = {
        tipo: values.tipo,
        dataRecebimento: values.dataRecebimento,
        moedaOrigem: values.moedaOrigem,
        valorOriginal: values.valorOriginal,
        pais: COUNTRY_OF[values.moedaOrigem],
        anoBase: Number.parseInt(values.dataRecebimento.slice(0, 4), 10),
        pagador: values.pagador,
        categoria: values.categoria,
        tag: values.tag,
        descricao: values.descricao,
      }

      const changedInputs: Partial<Entry> = {}
      if (values.dataRecebimento !== current.dataRecebimento) {
        changedInputs.dataRecebimento = values.dataRecebimento
      }
      if (values.moedaOrigem !== current.moedaOrigem) {
        changedInputs.moedaOrigem = values.moedaOrigem
      }
      if (values.valorOriginal !== current.valorOriginal) {
        changedInputs.valorOriginal = values.valorOriginal
      }
      const inputsChanged = editingInvalidatesSnapshot(changedInputs)

      if (inputsChanged) patch.snapshot = emptySnapshot()
      updateEntryAction(id, patch)
      rememberMetadata({
        pagador: values.pagador,
        categoria: values.categoria,
        tag: values.tag,
        descricao: values.descricao,
      })
      rememberDefaultCurrency(values.moedaOrigem)
      if (inputsChanged) void computeAndApply(id)
      return { ...current, ...patch } as Entry
    },
    [computeAndApply, rememberDefaultCurrency, rememberMetadata, updateEntryAction],
  )

  const deleteEntry = useCallback(
    (id: string) => {
      removeEntry(id)
    },
    [removeEntry],
  )

  const recalculateEntry = useCallback(
    async (id: string) => {
      await computeAndApply(id)
    },
    [computeAndApply],
  )

  const recalculateAll = useCallback(async () => {
    const ids = useEntriesStore.getState().entries.map((e) => e.id)
    setBulkProgress({ total: ids.length, done: 0 })
    try {
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        if (id === undefined) continue
        await computeAndApply(id)
        setBulkProgress({ total: ids.length, done: i + 1 })
      }
    } finally {
      setBulkProgress(null)
    }
  }, [computeAndApply])

  // Pure helper so the UI can show "X lançamentos serão atualizados" before
  // the user commits the rename.
  // An empty `name` is a valid query that matches entries with no pagador
  // set yet — surfaces the "backfill blank pagadores" case.
  const countEntriesByPagador = useCallback((name: string): number => {
    const target = name.trim()
    return useEntriesStore
      .getState()
      .entries.filter((e) => (e.pagador ?? '').trim() === target).length
  }, [])

  const renamePagador = useCallback(
    (oldName: string, newName: string): number => {
      const oldTrim = oldName.trim()
      const newTrim = newName.trim()
      // The destination must be a non-empty name (renaming "to empty"
      // would erase the pagador on every match, destructive and out of
      // scope). The source can be empty so the user can backfill the
      // pagador on lançamentos that currently have none.
      if (!newTrim || oldTrim === newTrim) return 0

      const state = useEntriesStore.getState()
      const now = new Date().toISOString()
      let count = 0
      const next = state.entries.map((entry) => {
        if ((entry.pagador ?? '').trim() === oldTrim) {
          count += 1
          return { ...entry, pagador: newTrim, atualizadoEm: now }
        }
        return entry
      })
      if (count === 0) return 0

      state.replace(next)

      // Keep the autocomplete history in sync: drop the old name (if any)
      // since no entry uses it anymore, and make sure the new one is present.
      const settings = useSettingsStore.getState().settings
      const filtered = oldTrim
        ? settings.knownPagadores.filter((p) => p !== oldTrim)
        : settings.knownPagadores
      const withNew = filtered.includes(newTrim) ? filtered : [...filtered, newTrim]
      if (
        withNew.length !== settings.knownPagadores.length ||
        withNew.some((p, i) => settings.knownPagadores[i] !== p)
      ) {
        patchSettings({ knownPagadores: withNew })
      }

      return count
    },
    [patchSettings],
  )

  return {
    createEntry,
    updateEntry,
    deleteEntry,
    recalculateEntry,
    recalculateAll,
    renamePagador,
    countEntriesByPagador,
    bulkProgress,
  }
}

function addUnique(list: string[], value: string | undefined): string[] {
  if (typeof value !== 'string' || value.trim().length === 0) return list
  const normalized = value.trim()
  if (list.includes(normalized)) return list
  return [...list, normalized]
}
